import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Text } from '@myrealtrip/web-ui';

import type { BadgeType } from '~types/common';

interface Props {
  type: BadgeType;
  text: string;
  index: number;
  className?: string;
}

const HighlightBadge = ({ type, text, index, className = '' }: Props) => {
  const theme = useTheme();

  const getColorSet = (type: BadgeType) => {
    switch (type) {
      case 'shortest':
        return { background: theme.colors.green.s500, edge: theme.colors.green.s600 };
      case 'lowest':
        return { background: theme.colors.blue.s500, edge: theme.colors.blue.s600 };
      case 'promotion':
      default:
        return { background: theme.colors.red.s400, edge: theme.colors.red.s700 };
    }
  };

  const { background, edge } = getColorSet(type);

  return (
    <BadgeContainer className={className}>
      <Rectangular color={background} hideCurve={index !== 0}>
        <BadgeText typography="captionBold13">{text}</BadgeText>
        {index === 0 && (
          <BadgeEdge>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="7"
              height="8"
              viewBox="0 0 7 8"
              fill="none"
            >
              <path d="M0 0H7V6.66928C7 7.13096 6.42773 7.34598 6.12371 6.99853L0 0Z" fill={edge} />
            </svg>
          </BadgeEdge>
        )}
      </Rectangular>
    </BadgeContainer>
  );
};

const BadgeContainer = styled.div`
  position: relative;
  width: fit-content;
`;

const Rectangular = styled.div<{ color: string; hideCurve: boolean }>`
  background-color: ${({ color }) => color};
  padding: 2.5px 6px 3px;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  ${({ hideCurve }) => hideCurve && 'border-bottom-left-radius: 6px;'}
`;

const BadgeText = styled(Text)`
  color: ${({ theme }) => theme.colors.white};
`;

const BadgeEdge = styled.div`
  position: absolute;
  bottom: -10px;
  left: 0;
`;

export default HighlightBadge;
